import { ClipboardCheck } from 'lucide-react';

import { FormField } from './FormField';
import { FormSection } from './FormSection';
import { FORM_SECTIONS, type ProjectFormValues } from '../../schemas/project.schema';
import { Button } from '@/shared/components/ui/button';
import { Select } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { messages } from '@/shared/constants/messages';
import type { MinistryMember } from '@/shared/types/domain';

interface SectionReviewProps {
  values: ProjectFormValues;
  members: MinistryMember[];
  errors: Partial<Record<keyof ProjectFormValues, string>>;
  /** O que ainda falta, na ordem das seções, com âncora para cada pendência. */
  missing: { sectionId: string; label: string }[];
  canSubmit: boolean;
  isSubmitting: boolean;
  /**
   * A data está contraditória (`lib/schedule.ts`) — e não é "falta preencher".
   *
   * Sem isto, o botão apagado explicava a si mesmo com a frase errada: as
   * cinco seções aparecem completas, o índice marca 4 de 4, e a pessoa fica
   * procurando um campo vazio que não existe.
   */
  dateIssue?: string | null;
  /** Modo ajuste (decisão 48): o verbo é reenviar, e o documento já foi lido. */
  isAdjusting?: boolean;
  onChangeText: (patch: Partial<ProjectFormValues>) => void;
  onChangeNow: (patch: Partial<ProjectFormValues>) => void;
  /** Abre a revisão. **Não envia** — quem envia é o botão de dentro da folha. */
  onReview: () => void;
}

/**
 * A última seção do documento: quem responde pelo projeto, o que mais precisa
 * ser dito, e a porta de saída.
 *
 * O resumo dos campos e o alerta de antecedência viviam aqui e foram para a
 * folha de revisão (`ReviewSheet`). O motivo é a posição: eram os dois últimos
 * blocos antes do botão, exatamente onde a rolagem de quem já decidiu enviar
 * acelera. Como conteúdo da folha, eles passaram de rodapé a pedágio.
 */
export function SectionReview({
  values,
  members,
  errors,
  missing,
  canSubmit,
  isSubmitting,
  isAdjusting = false,
  dateIssue = null,
  onChangeText,
  onChangeNow,
  onReview,
}: SectionReviewProps) {
  return (
    <FormSection id="responsavel-e-revisao" number={5} title={messages.projectSections.review}>
      <FormField
        label="Pessoa responsável"
        required
        error={errors.responsible_profile_id}
        hint="Quem a liderança procura para falar deste projeto. A lista traz quem tem vínculo ativo no ministério."
      >
        <Select
          value={values.responsible_profile_id ?? ''}
          onChange={(event) =>
            onChangeNow({ responsible_profile_id: event.target.value || null })
          }
        >
          <option value="">Selecione…</option>
          {members.map((member) => (
            <option key={member.profile_id} value={member.profile_id}>
              {member.full_name}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField
        label={messages.projectSections.extraInfo}
        hint="Qualquer coisa que ajude quem vai analisar. Não há anexo de arquivo nesta versão."
      >
        <Textarea
          rows={3}
          maxLength={4000}
          value={values.extra_info ?? ''}
          onChange={(event) => onChangeText({ extra_info: event.target.value || null })}
        />
      </FormField>

      {missing.length > 0 ? (
        <div className="animate-reveal rounded-md border border-border bg-muted/50 p-3 text-sm">
          <p className="font-medium">{messages.form.missingBeforeSubmit}</p>
          <ul className="stagger mt-1.5 space-y-1">
            {missing.map((item) => (
              <li key={`${item.sectionId}-${item.label}`}>
                <a
                  href={`#${item.sectionId}`}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  {item.label}
                  <span className="sr-only">
                    {' '}
                    (ir para a seção{' '}
                    {FORM_SECTIONS.find((section) => section.id === item.sectionId)?.title})
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* O botão vive só aqui, no fim do documento, e nunca numa barra fixa —
          chegar até ele é parte de terminar de escrever. O que ele faz mudou:
          abre a revisão. O rótulo diz isso ("Revisar e enviar") porque um botão
          que promete enviar e abre uma janela é o mesmo que mentir uma vez por
          projeto. */}
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <Button type="button" size="lg" disabled={!canSubmit || isSubmitting} onClick={onReview}>
          <ClipboardCheck aria-hidden="true" className="size-4" />
          {isAdjusting ? messages.form.reviewAndResubmit : messages.form.reviewAndSubmit}
        </Button>
        {!canSubmit ? (
          <p className="text-xs text-muted-foreground">
            {dateIssue ? (
              <>
                {dateIssue}{' '}
                <a href="#data-e-local" className="text-primary underline-offset-2 hover:underline">
                  Ir para {messages.projectSections.schedule}
                </a>
              </>
            ) : (
              'O envio libera quando as seções obrigatórias estiverem completas.'
            )}
          </p>
        ) : null}
      </div>
    </FormSection>
  );
}
